import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import { libelleVilleDate } from "utils/oumrahVilleDepart";
import TimelineCycle from "components/TimelineCycle";
import DocumentsParticipant from "components/DocumentsParticipant";
import DesignationCouple from "components/DesignationCouple";
import FormulaireInscriptionOumrah from "components/FormulaireInscriptionOumrah";

const statutLabel = {
  en_attente: "En attente", validee: "Validée",
  confirmee: "Confirmée", annulee: "Annulée",
};
const statutColor = {
  en_attente: "#888780", validee: "#378ADD",
  confirmee: "#1D9E75", annulee: "#e53935",
};

function OumrahDirecteur() {
  const [mesInscriptions, setMesInscriptions] = useState([]);
  const [docsParInscription, setDocsParInscription] = useState({});
  const [paieForm, setPaieForm] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR");

  const chargerDocsInscription = async (inscId) => {
    try {
      const res = await api.get("/oumrah/documents/inscription/" + inscId);
      setDocsParInscription((d) => ({ ...d, [inscId]: res.data }));
    } catch (err) {
      // silencieux
    }
  };

  const enregistrerCoupleDir = async (inscId, p1, p2) => {
    await api.put("/oumrah/inscriptions/" + inscId + "/couple", {
      participant1_id: p1,
      participant2_id: p2,
    });
    fetchMesInscriptions();
  };

  const toggleDocumentDir = async (inscId, participantId, typeId, fourni) => {
    try {
      await api.post("/oumrah/documents/cocher", {
        participant_id: participantId,
        type_document_id: typeId,
        fourni,
      });
      chargerDocsInscription(inscId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    }
  };

  const docsDuParticipant = (inscId, participantId) => {
    const data = docsParInscription[inscId];
    if (!data) return [];
    const p = data.participants.find((x) => x.id === participantId);
    return p ? p.documents : [];
  };

  const fetchMesInscriptions = async () => {
    try {
      const res = await api.get("/oumrah/inscriptions");
      setMesInscriptions(res.data);
      res.data.forEach((insc) => chargerDocsInscription(insc.id));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchMesInscriptions(); }, []);

  const handleOpenNew = () => {
    setEditId(null);
    setOpen(true);
  };

  const handleOpenEdit = (id) => {
    setEditId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const setPaieChamp = (inscId, champ, valeur) => {
    setPaieForm((f) => ({ ...f, [inscId]: { ...(f[inscId] || {}), [champ]: valeur } }));
  };

  const ajouterPaiementDir = async (inscId) => {
    const form = paieForm[inscId] || {};
    if (!form.montant || parseFloat(form.montant) <= 0) {
      alert("Saisissez un montant valide.");
      return;
    }
    try {
      await api.post("/oumrah/paiements", {
        inscription_id: inscId,
        montant: parseFloat(form.montant),
        mode: form.mode || null,
      });
      setPaieForm((f) => ({ ...f, [inscId]: { montant: "", mode: "" } }));
      fetchMesInscriptions();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'ajout du paiement");
    }
  };

  const validerInscription = async (id) => {
    if (!window.confirm("Valider cette inscription ?")) return;
    try {
      await api.put("/oumrah/inscriptions/" + id + "/statut", { statut: "validee" });
      fetchMesInscriptions();
    } catch (err) {
      alert((err.response && err.response.data && err.response.data.message) || "Erreur lors de la validation");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">Mes inscriptions Oumrah</MDTypography>
                <MDButton variant="outlined" color="white" size="small" onClick={handleOpenNew}>
                  <Icon>add</Icon>&nbsp;Nouvelle inscription
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={2}>
                  {mesInscriptions.map((i) => (
                    <Grid item xs={12} md={6} key={i.id}>
                      <Card sx={{ p: 0, border: "1px solid #e0e0e0", overflow: "hidden" }}>
                        <MDBox sx={{ bgcolor: "#f8f9fa", borderBottom: "1px solid #eee" }} px={2.5} py={1.5}>
                          <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="h6">{i.session_libelle}</MDTypography>
                            <Chip label={statutLabel[i.statut]} size="small"
                              sx={{ bgcolor: statutColor[i.statut], color: "white", fontWeight: "bold" }} />
                          </MDBox>
                          <MDTypography variant="caption" color="text">
                            {i.nb_participants} participant(s) · <span style={{ textTransform: "capitalize" }}>{i.type_inscription}</span>
                          </MDTypography>
                        </MDBox>

                        <MDBox px={2.5} py={2}>
                          <MDTypography variant="overline" color="text" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>
                            Participants
                          </MDTypography>
                          {i.participants?.map((p) => (
                            <MDBox key={p.id} mt={1} mb={1.5} pb={1.5} sx={{ borderBottom: "1px dashed #eee", "&:last-child": { borderBottom: "none", mb: 0, pb: 0 } }}>
                              <MDBox display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
                                <MDTypography variant="button" fontWeight="bold">
                                  {p.prenom} {p.nom ? p.nom.toUpperCase() : ""}
                                </MDTypography>
                                <MDTypography variant="button" fontWeight="bold" color="info">
                                  {fmt((p.tarif_montant ? parseFloat(p.tarif_montant) : 0) + (p.besoin_carte_sejour ? parseFloat(i.supplement_carte_sejour || 0) : 0))} €
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" gap={0.75} alignItems="center" flexWrap="wrap" mt={0.5} mb={0.5}>
                                {p.tarif_ville_depart && (
                                  <Chip
                                    size="small"
                                    icon={<Icon fontSize="small">flight_takeoff</Icon>}
                                    label={libelleVilleDate(p.tarif_ville_depart, p.tarif_ville_depart_date)}
                                    sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: "bold", "& .MuiChip-icon": { color: "#1565c0" } }}
                                  />
                                )}
                                {p.tarif_type_chambre && (
                                  <Chip
                                    size="small"
                                    icon={<Icon fontSize="small">bed</Icon>}
                                    label={{
                                  double: "Chambre double",
                                  triple: "Chambre triple",
                                  quadruple: "Chambre quadruple",
                                }[p.tarif_type_chambre] || p.tarif_libelle || "Sans tarif"}
                                    sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: "bold", "& .MuiChip-icon": { color: "#6a1b9a" } }}
                                  />
                                )}
                                {!p.tarif_type_chambre && (
                                  <MDTypography variant="caption" color="text">{p.tarif_libelle || "Sans tarif"}</MDTypography>
                                )}
                              </MDBox>
                              {p.nationalite && (
                                <MDTypography variant="caption" color="text" display="block">
                                  {p.nationalite}
                                </MDTypography>
                              )}
                              {i.statut !== "annulee" && (
                                <MDBox mt={0.5}>
                                  <DocumentsParticipant
                                    documents={docsDuParticipant(i.id, p.id)}
                                    onToggle={(typeId, fourni) => toggleDocumentDir(i.id, p.id, typeId, fourni)}
                                  />
                                </MDBox>
                              )}
                            </MDBox>
                          ))}
                        </MDBox>

                        {i.statut !== "annulee" && (
                          <MDBox mx={2.5} mb={2} sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", p: 2 }}>
                            <MDTypography variant="overline" color="text" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>
                              Paiement
                            </MDTypography>
                            <MDBox display="flex" justifyContent="space-between" alignItems="baseline" mt={0.5} mb={1}>
                              <MDTypography variant="caption" color="text">
                                Payé : <b>{fmt(i.montant_paye)} €</b>
                              </MDTypography>
                              <MDTypography variant="button" sx={{ color: (i.reste_a_payer <= 0 ? "#1D9E75" : "#e67e22"), fontWeight: "bold" }}>
                                Reste : {fmt(i.reste_a_payer)} €
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              sx={{
                                height: 6, borderRadius: 3, bgcolor: "#e0e0e0", overflow: "hidden", mb: 1.5,
                              }}
                            >
                              <MDBox
                                sx={{
                                  height: "100%",
                                  width: (i.montant_paye && (i.montant_paye + i.reste_a_payer) > 0
                                    ? Math.min(100, (i.montant_paye / (i.montant_paye + i.reste_a_payer)) * 100)
                                    : 0) + "%",
                                  bgcolor: i.reste_a_payer <= 0 ? "#1D9E75" : "#2196f3",
                                  transition: "width 0.3s",
                                }}
                              />
                            </MDBox>
                            <MDBox display="flex" gap={1} alignItems="center">
                              <TextField
                                label="Montant €" type="number" size="small"
                                sx={{ width: 110, bgcolor: "white" }}
                                value={(paieForm[i.id] && paieForm[i.id].montant) || ""}
                                onChange={(e) => setPaieChamp(i.id, "montant", e.target.value)}
                              />
                              <FormControl size="small" sx={{ minWidth: 120, bgcolor: "white" }}>
                                <InputLabel id={"mode-" + i.id}>Mode</InputLabel>
                                <Select
                                  labelId={"mode-" + i.id}
                                  label="Mode"
                                  value={(paieForm[i.id] && paieForm[i.id].mode) || ""}
                                  onChange={(e) => setPaieChamp(i.id, "mode", e.target.value)}
                                  sx={{ height: 40 }}
                                >
                                  <MenuItem value="espece">Espèce</MenuItem>
                                  <MenuItem value="virement">Virement</MenuItem>
                                  <MenuItem value="cheque">Chèque</MenuItem>
                                </Select>
                              </FormControl>
                              <MDButton size="small" color="info" onClick={() => ajouterPaiementDir(i.id)}>
                                <Icon>add</Icon>
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        )}

                        <MDBox px={2.5} pb={1}>
                          <TimelineCycle statut={i.statut} />
                          <DesignationCouple
                            inscription={i}
                            onSave={(p1, p2) => enregistrerCoupleDir(i.id, p1, p2)}
                          />
                        </MDBox>

                        <MDBox px={2.5} pb={2.5} pt={1} sx={{ borderTop: "1px solid #f0f0f0" }}>
                          {i.statut === "en_attente" && (
                            <MDButton size="small" color="success" fullWidth sx={{ mb: 1 }}
                              onClick={() => validerInscription(i.id)}>
                              <Icon>check_circle</Icon>&nbsp;Valider mon inscription
                            </MDButton>
                          )}
                          {i.statut !== "annulee" && (
                            <MDBox display="flex" gap={1}>
                              <MDButton size="small" color="info" variant="outlined" fullWidth onClick={() => handleOpenEdit(i.id)}>
                                <Icon>edit</Icon>&nbsp;Modifier
                              </MDButton>
                            </MDBox>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                  {mesInscriptions.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune inscription. Cliquez sur "Nouvelle inscription".
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <FormulaireInscriptionOumrah
          open={open}
          onClose={handleClose}
          editId={editId}
          onSaved={() => fetchMesInscriptions()}
        />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahDirecteur;
